import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { MatchResult, Lineup } from '@stanwars/sim';
import { PixelText } from '../ui/PixelText';
import { PixelButton } from '../ui/PixelButton';
import { PixelPanel } from '../ui/PixelPanel';
import { BattleScene } from '../battle/BattleScene';
import { api, type Replay as ReplayData } from '../api/client';

/** Public page — no auth required. */
export function Replay() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [replay, setReplay] = useState<ReplayData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [startFrame, setStartFrame] = useState(0);
  const [totalFrames, setTotalFrames] = useState(60);

  useEffect(() => {
    if (!id) return;
    void (async () => {
      try {
        const data = await api.match.replay(id);
        setReplay(data);
        const result = data.result as MatchResult;
        const lastTick = result.events.length > 0
          ? result.events[result.events.length - 1]!.t
          : 0;
        setTotalFrames(Math.max(60, (lastTick + 1) * 30));
      } catch (e) {
        setError(String(e));
      }
    })();
  }, [id]);

  const replayUrl = `${window.location.origin}${window.location.pathname}`;

  const handleCopyLink = () => {
    void navigator.clipboard.writeText(replayUrl);
  };

  const handleTweet = () => {
    const text = encodeURIComponent(`Check out this Stan Wars replay! ${replayUrl}`);
    window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank', 'noopener,noreferrer');
  };

  if (error) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center gap-4 p-6">
        <PixelText size="xl" color="pink">{t('replay.title')}</PixelText>
        <PixelText size="sm" color="yellow">{error}</PixelText>
      </main>
    );
  }

  if (!replay) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center gap-4 p-6">
        <PixelText size="xl" color="pink">{t('replay.title')}</PixelText>
        <PixelText size="sm" color="cyan">{t('replay.loading')}</PixelText>
      </main>
    );
  }

  const result = replay.result as MatchResult;
  const lineupA = (replay.lineupA && typeof replay.lineupA === 'object' && 'slots' in replay.lineupA)
    ? replay.lineupA as Lineup
    : null;
  const lineupB = (replay.lineupB && typeof replay.lineupB === 'object' && 'slots' in replay.lineupB)
    ? replay.lineupB as Lineup
    : null;

  if (!lineupA || !lineupB) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center gap-4 p-6">
        <PixelText size="xl" color="pink">{t('replay.title')}</PixelText>
        <PixelText size="sm" color="yellow">{t('replay.error')}</PixelText>
      </main>
    );
  }

  return (
    <main className="min-h-screen flex flex-col gap-4 p-4">
      <div className="flex items-center gap-3">
        <PixelText size="xl" color="pink">{t('replay.title')}</PixelText>
        <PixelText size="sm" color="cyan">{replay.kind} • {replay.createdAt.slice(0, 10)}</PixelText>
      </div>

      <BattleScene
        key={startFrame}
        result={result}
        lineupA={lineupA}
        lineupB={lineupB}
        startFrame={startFrame}
      />

      <PixelPanel>
        <PixelText size="sm" color="cyan" className="block mb-2">
          {t('replay.timeline', { frame: startFrame, total: totalFrames })}
        </PixelText>
        <input
          type="range"
          min={0}
          max={totalFrames}
          step={1}
          value={startFrame}
          onChange={e => setStartFrame(Number(e.target.value))}
          className="w-full"
        />
      </PixelPanel>

      <PixelPanel>
        <PixelText size="sm" color="cyan" className="block mb-2">{t('replay.share')}</PixelText>
        <div className="flex gap-2">
          <PixelButton variant="ghost" className="text-xs" onClick={handleCopyLink}>
            {t('replay.copy_link')}
          </PixelButton>
          <PixelButton variant="ghost" className="text-xs" onClick={handleTweet}>
            {t('replay.tweet')}
          </PixelButton>
        </div>
      </PixelPanel>
    </main>
  );
}
